import { useTranslation } from "next-i18next";
import { useQueryClient } from "react-query";
import { useDeleteCommentMutation } from "src/api/client/comment-v2.api";
import { mapInfiniteQueryPages } from "src/components/common/comments-area/utils/map-infinite-query-pages";
import { EnumToastStatus, useWfToast } from "src/hooks/use-wf-toast";
import { TComment } from "../comment/comment.types";
import { TCommentsPaginatedDataType } from "../utils/create-initial-replies-page-data";
import { markAsDeleted } from "../utils/mark-as-deleted";
import { updateCommentInfiniteQueryCache } from "../utils/update-comment-infinite-query-cache";

const useUpdateInfiniteQueryCacheDeleteComment = ({ entityId, comment }: { comment: TComment; entityId: string }) => {
    const queryClient = useQueryClient();

    const handleDeletedComment = (data: TCommentsPaginatedDataType | undefined) => {
        if (data?.pages) {
            const mapCommentsCallback = (comments: TComment[]) => {
                return markAsDeleted({ comments, commentIdToDelete: comment.commentId });
            };

            return {
                pages: mapInfiniteQueryPages({ pages: data.pages, callback: mapCommentsCallback }),
                pageParams: data.pageParams,
            };
        }
    };

    const updateInfiniteQueryCacheDeleteComment = () =>
        updateCommentInfiniteQueryCache({
            queryClient,
            comment,
            entityId,
            handleRootComment: handleDeletedComment,
            handleReplyComment: handleDeletedComment,
        });

    return { updateInfiniteQueryCacheDeleteComment };
};

export const useDeleteComment = ({ entityId, comment }: { comment: TComment; entityId: string }) => {
    const { t } = useTranslation(["common", "comment"]);
    const toast = useWfToast();

    const { updateInfiniteQueryCacheDeleteComment } = useUpdateInfiniteQueryCacheDeleteComment({ entityId, comment });

    const deleteComment = useDeleteCommentMutation({
        commentId: comment.commentId,
        options: {
            onSuccess: () => {
                updateInfiniteQueryCacheDeleteComment();
                toast({ status: EnumToastStatus.Success, title: t("comment-deleted", { ns: "comment" }) });
            },
            onError: () => {
                toast({ status: EnumToastStatus.Error, title: t("general-error") });
            },
        },
    });

    return {
        deleteComment,
    };
};
